import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { PasswordInput } from "@admin/components/atoms/Input/PasswordInput";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  Company,
  passwordValidation,
  repasswordValidation,
  updateCompany,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface EditAdminPasswordModalProps extends ModalProps {
  companyData: Company;
}

export function EditAdminPasswordModal({
  companyData,
  ...props
}: EditAdminPasswordModalProps) {
  const { t } = useTranslation();
  const mutation = useMutation({
    mutationFn: updateCompany,
    onSuccess: () => {
      reset();
      props?.onClose?.();
      toast.success(t("settings.The admin password has been changed"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("settings.Unable to change the admin password"), {
        duration: 4000,
      });
    },
  });
  const passwordSchema = z
    .object({
      password: passwordValidation(t),
      confirmPassword: repasswordValidation(t),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("common.Passwords must match"),
      path: ["confirmPassword"],
    });
  type PasswordSchemaData = z.infer<typeof passwordSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PasswordSchemaData>({
    resolver: zodResolver(passwordSchema),
  });

  const onSubmit = (data: PasswordSchemaData) => {
    mutation.mutate({ ...companyData, password: data.password });
  };

  return (
    <Modal
      {...props}
      title={t("settings.Edit Admin Password")}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={props?.onClose}
            disabled={mutation.isPending}
          >
            {t("common.Discard")}
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={handleSubmit(onSubmit)}
          >
            {t("common.Save Changes")}
          </Button>
        </>
      }
    >
      <form>
        <div className="flex flex-col w-100 gap-5">
          <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
            <label className="form-label flex items-center gap-1 max-w-56 !font-normal">
              {t("settings.Admin Password")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="d-flex flex-col w-full">
              <PasswordInput
                placeholder={t("common.Enter Password")}
                {...register("password")}
              />
              <ErrorMessage message={errors.password?.message as string} />
            </div>
          </div>

          <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
            <label className="form-label flex items-center gap-1 max-w-56 !font-normal">
              {t("settings.Admin Password Again")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="d-flex flex-col w-full">
              <PasswordInput
                placeholder={t("common.Enter Password Again")}
                {...register("confirmPassword")}
              />
              <ErrorMessage
                message={errors.confirmPassword?.message as string}
              />
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
}
